import type {
  Trait,
  LifeChoice,
  WorldDetail,
  Relationship,
  Dossier,
} from "../types";
import type { NarrativeContext, NarrativeResult } from "../narrative";
import {
  fill,
  pick,
  pickDistinct,
  listJoin,
  clamp,
  INTEREST_PHRASE,
  STRENGTH_PHRASE,
  AMBITION_PHRASE,
  LIFE_PATH_PHRASE,
  influenceLead,
} from "./helpers";
import {
  buildUniverseDescription,
  buildWorldDetails,
  STRENGTH_TRAIT_MAP,
} from "./shared";
import { generateAlienName, generatePersonName } from "./names";
import {
  STRANGE_IDENTITIES,
  STRANGE_TITLES_PARTS,
  STRANGE_TAGLINE_PARTS,
  OCCUPATION_PARTS,
  LIFE_PATH_MANNER,
  ORIGIN_PARTS,
  TURNING_POINT_PARTS,
  TODAY_PARTS,
  STRANGE_CHOICES_BANK,
  STRANGE_SKILLS_BANK,
  STRANGE_MOTIVATIONS_BANK,
  STRANGE_RELATIONSHIPS_BANK,
  STRANGE_ACHIEVEMENTS_BANK,
  STRANGE_FAILURES_BANK,
  STRANGE_DILEMMAS_BANK,
} from "./strange.banks";

/**
 * 12+ physical derivation rules deriving senses, body plan, locomotion,
 * social structure, economy, and daily rituals directly from universe constants.
 */
function derivePhysicsRealities(ctx: NarrativeContext) {
  const { config, profile } = ctx.universe;
  const rng = ctx.stream("physics_derivations");

  const rules: { label: string; text: string }[] = [];

  // 1. Gravity derivation: morphology and locomotion
  if (config.gravity > 2.5) {
    const v = pick(
      [
        "High gravitational pressure forces wide, flattened skeletal structures, quadrupedal posture, and movement paced like a slow, deliberate ceremonial dance.",
        "Under intense gravity, locomotion relies on pneumatic cable winches, low-slung crawlers, and wide magnetic anchor shoes.",
        "Skeletal density is triple terrestrial baseline, making falling fatal and turning jump mechanics into an intricate art of balanced counterweights.",
        "Settlements hug deep granite trenches where counterweighted tension cables anchor buildings directly to tectonic bedplates.",
      ],
      rng
    );
    rules.push({ label: "High-G Locomotion", text: v });
  } else if (config.gravity < 0.6) {
    const v = pick(
      [
        "Low gravity fosters elongated, gossamer limbs and prehensile wing-flaps, allowing inhabitants to cross kilometers of open canyon with light muscle effort.",
        "Locomotion is three-dimensional: drifting between suspension terraces on woven mica sails and using pneumatic air-burst thrusters.",
        "Inhabitants grow tall and fragile, relying on fluid-damped harnesses to absorb sudden kinetic shocks during thermal landings.",
        "Architecture rises in slender, spire-like lattices held together by minimal carbon tethers and buoyant gas-filled roofs.",
      ],
      rng
    );
    rules.push({ label: "Low-G Locomotion", text: v });
  } else {
    const v = pick(
      [
        "Balanced terrestrial gravity supports versatile bipedal agility, combining mountain-ridge running with precision glider maneuvering.",
        "Locomotion balances grounded foot travel across basalt ledges with thermal glider sweeps along canyon updrafts.",
        "Musculoskeletal structure adapts dynamically to seasonal atmospheric pressure shifts without structural deformity.",
        "Civil infrastructure bridges ground paths and suspension lines with equal mechanical ease.",
      ],
      rng
    );
    rules.push({ label: "Standard Gravity Locomotion", text: v });
  }

  // 2. Topology derivation: horizons, perspective, and navigation
  if (config.topology === "toroidal") {
    const v = pick(
      [
        "Toroidal global geometry produces wrap-around horizons; looking directly zenith-ward reveals the upside-down settlements and glowing hearths of the opposite loop.",
        "Navigation relies on continuous loop-closure theorems, where traveling straight in any single direction returns the wayfarer to their point of origin.",
        "Religious folklore treats space as an unbroken serpent, with ceremonies timed to the alignment of mirrored cities across the sky.",
        "Cartography rejects flat projection entirely, using rotating brass donuts to plot migratory trade flumes.",
      ],
      rng
    );
    rules.push({ label: "Toroidal Horizon", text: v });
  } else if (config.topology === "closed") {
    const v = pick(
      [
        "Closed spherical 3-space creates a bounded sky where powerful optical telescopes can view distant ghost images of the planet's own ancestral past.",
        "Horizon perspective curves upward at extreme distances, creating the optical sensation of living inside an immense, glowing bowl.",
        "Clan cosmologies emphasize circular return and finite matter stewardship, treating every atom as an irreplaceable generational relic.",
        "Long-range communication utilizes spherical harmonic echoes bounced off the upper atmospheric boundary.",
      ],
      rng
    );
    rules.push({ label: "Closed Geometry", text: v });
  } else if (config.topology === "open") {
    const v = pick(
      [
        "Hyperbolic open geometry causes light and horizons to flare outward indefinitely, creating a terrifying, exhilarating sense of boundless void.",
        "Perspective stretches distant landforms wider than Euclid's axioms predict, demanding specialized curved lenses for accurate distance gauging.",
        "Philosophy in this world centers on infinite divergence; no path ever repeats, and every departure is acknowledged as permanent.",
        "Acoustic waves disperse rapidly into the widening open geometry, making visual light-signals the primary civic telegraph.",
      ],
      rng
    );
    rules.push({ label: "Hyperbolic Flaring", text: v });
  } else {
    const v = pick(
      [
        "Flat Euclidean geometry offers clean, straight lines of sight that extend uninterrupted to the knife-edge boundary of the planetary horizon.",
        "Navigation uses traditional right-angle triangulation, with surveyors placing brass azimuth monuments along every major ridge.",
        "Clan architecture favors square stone terraces and orthogonal street grids carved into volcanic bedrock.",
        "Astronomers enjoy undistorted parallax measurements, tracking planetary transits with pristine geometric certainty.",
      ],
      rng
    );
    rules.push({ label: "Euclidean Horizon", text: v });
  }

  // 3. Dimensions derivation: physics constraints
  if (config.dimensions === 2) {
    const v = pick(
      [
        "Planar 2D existence demands strictly non-overlapping biological pathways and edge-based sensory organs that process reality in linear scans.",
        "Social structures exist in tiered perimeter lines; passing another person requires coordinated clearance rotations on shared ribbons.",
        "Architecture consists of segmented linear chambers where acoustic pulses travel along flat structural edges.",
        "Matter is organized into delicate mono-molecular sheets, with chemistry constrained to planar molecular bonds.",
      ],
      rng
    );
    rules.push({ label: "Planar Chemistry", text: v });
  } else if (config.dimensions === 4) {
    const v = pick(
      [
        "Hyper-dimensional 4D space allows objects to rotate along planes unseen by 3D eyes; inhabitants possess dual-pupil organs tuned to hyper-depth.",
        "Living structures feature interior chambers that open into unexpected volumetric pockets without expanding external exterior walls.",
        "Locomotion involves subtle lateral tilts into 4D coordinate axes to step over seemingly impenetrable solid 3D barriers.",
        "Gravitational attenuation scales rapidly at 1/r³, confining civilization to extremely tight, dense orbital or planetary clusters.",
      ],
      rng
    );
    rules.push({ label: "Hyper-Spatial Depth", text: v });
  } else {
    const v = pick(
      [
        "Three spatial dimensions provide stable 1/r² force laws, enabling reliable planetary orbits, spherical chemistry, and rich volumetric biomes.",
        "Volumetric movement allows full six-degree maneuvering, from high-altitude glider swoops to deep vertical chasm descents.",
        "Architecture takes full advantage of three-dimensional suspension, hanging tiered terraces across vertical canyon facades.",
        "Organic chemistry thrives on tetrahedral carbon bonds, creating complex cellular membranes and flexible biological tissues.",
      ],
      rng
    );
    rules.push({ label: "3D Volumetric Balance", text: v });
  }

  // 4. Electromagnetism & Chemistry derivation
  if (config.electromagnetism > 1.3) {
    const v = pick(
      [
        "Elevated electromagnetic coupling creates hyper-rigid crystalline molecular lattices; rocks ring like silver bells and skin has a metallic sheen.",
        "Static discharges constantly flicker along ridge points, requiring grounding chains on all clothing and glider harnesses.",
        "Chemical reactions occur with explosive rapidity, demanding cold-water dampening baths for even simple domestic food preparation.",
        "Vision is shifted toward ultraviolet and polarized spectra, seeing electrical tension fields shimmering across the open air.",
      ],
      rng
    );
    rules.push({ label: "Hyper-Electromagnetic Lattice", text: v });
  } else if (config.electromagnetism < 0.75) {
    const v = pick(
      [
        "Weak electromagnetic coupling yields soft, gelatinous matter and pliable, rubber-like mineral formations that absorb massive impacts.",
        "Bio-electricity operates at faint, whisper-level currents; communication relies heavily on tactile chemosensory touch rather than neural nerves.",
        "Atmospheric lightning is non-existent, leaving nights pitch-black except for soft, low-energy biochemical bioluminescence.",
        "Metabolism is slow and cool, allowing organisms to survive for weeks on minimal chemical caloric intake.",
      ],
      rng
    );
    rules.push({ label: "Subdued Electromagnetic Bonding", text: v });
  } else {
    const v = pick(
      [
        "Balanced electromagnetic coupling enables rich chemical diversity, supporting both hard mineral skeletons and soft, sensitive nervous tissue.",
        "Bioluminescent fungi and quartz crystal refraction provide clean, steady illumination for subterranean and cliff-hanging settlements.",
        "Atmospheric electrical conductivity is mild, allowing reliable wire telegraphs and acoustic signal chimes across valley spans.",
        "Thermal and chemical balances allow varied cuisines based on fermentation, geothermal steaming, and mineral curing.",
      ],
      rng
    );
    rules.push({ label: "Balanced Chemical Paradigm", text: v });
  }

  // 5. Strong Nuclear Force derivation
  if (config.strongForce > 1.3) {
    const v = pick(
      [
        "An intense strong force binds diprotons, making primordial fusion ultra-rapid and leaving the cosmos with dense helium-rich stellar environments.",
        "Dense core chemistry produces heavy elements in abundance; tools are routinely forged from rare meteoric platinum and iridium alloys.",
        "Stellar bodies are compact and hot, casting brilliant bluish-white light that requires thick protective ocular nictitating membranes.",
        "Planetary geology is dense and mineral-rich, with mountains of solid heavy ore anchoring regional magnetic fields.",
      ],
      rng
    );
    rules.push({ label: "Nuclear Hyper-Binding", text: v });
  } else if (config.strongForce < 0.75) {
    const v = pick(
      [
        "Weak nuclear binding prevents multi-nucleon stability, leaving the cosmos dominated by pure hydrogen and extremely sparse heavier elements.",
        "Civilization relies on non-metallic engineering: compressed hydrocarbon resins, woven silicate fibers, and acoustic resonance apparatuses.",
        "Stars burn cool and diffuse, bathing the world in gentle amber-red illumination that never fades into harsh glare.",
        "Material wealth is measured not in gold or iron, but in trace samples of stable lithium and heavy isotopes conserved across millennia.",
      ],
      rng
    );
    rules.push({ label: "Hydrogen-Dominated Substrate", text: v });
  } else {
    const v = pick(
      [
        "Balanced nuclear constants sustain active stellar carbon cycles, enriching the planetary crust with granite, basalt, and organic minerals.",
        "Metallurgy and stonecraft coexist naturally, with smiths blending volcanic obsidian with bronze and forged iron fittings.",
        "Long-lived main sequence stars provide stable solar output, establishing dependable seasonal cycles for agriculture and migration.",
        "Deep geological mantle activity powers reliable geothermal vents that warm cliff habitats year-round.",
      ],
      rng
    );
    rules.push({ label: "Standard Nucleosynthesis", text: v });
  }

  // 6. Dark Energy & Expansion derivation
  if (config.darkEnergy > 3.0) {
    const v = pick(
      [
        "Rapid metric expansion has driven all neighboring galaxy clusters past the cosmic horizon; the night sky is utterly black except for local stars.",
        "Cultural mythologies treat their solar system as the sole island of light in an infinitely empty, rapidly fleeing universe.",
        "Long-range exploratory dreams focus inward on deep planetary strata rather than unattainable interstellar journeys.",
        "Civilization operates with a profound sense of self-reliance, knowing no external cosmic assistance will ever arrive.",
      ],
      rng
    );
    rules.push({ label: "Cosmic Horizon Isolation", text: v });
  } else if (config.darkEnergy === 0) {
    const v = pick(
      [
        "Zero dark energy allows gravity to hold distant galaxies in stable vista clusters, filling the night sky with radiant, glittering star webs.",
        "Starlight from hundreds of neighboring galaxies illuminates midnight landscapes, making total darkness an unnatural rarity.",
        "Clan folklore is rich with deep-sky navigation myths, viewing the heavens as a crowded, companionable archipelago of sibling worlds.",
        "Telescopic astronomers track gravitational interactions between galaxy clusters with unmatched clarity.",
      ],
      rng
    );
    rules.push({ label: "Clustered Night Sky", text: v });
  } else {
    const v = pick(
      [
        "Moderate dark energy expansion creates a steady cosmological backdrop, balancing local galactic cohesion with serene cosmic distance.",
        "The night sky displays a graceful balance: bright local constellations framed against the faint, shimmering fog of distant galactic bands.",
        "Civilization measures cosmic time in generational epochs, noting the imperceptible drift of neighboring constellations in ancient stone almanacs.",
        "Astrophysical philosophy balances grounded planetary duties with a gentle, romantic curiosity toward deep space.",
      ],
      rng
    );
    rules.push({ label: "Balanced Cosmic Backdrop", text: v });
  }

  // 7. Time Arrow derivation
  if (config.timeArrow === "reversed") {
    const v = pick(
      [
        "An inverted thermodynamic arrow means broken stone naturally reconstitutes, heat coalesces into concentrated embers, and memory faces the future.",
        "Perception experiences reality in reverse-causal ripples; individuals remember tomorrow's arrivals while yesterday fades into unformed potential.",
        "Workshops are places where scattered metal dust gathers spontaneously onto the anvil to form polished, pristine tools.",
        "Language structures place verbs of consequence before verbs of intent, acknowledging that the outcome pulls the cause into being.",
      ],
      rng
    );
    rules.push({ label: "Inverted Entropy Cadence", text: v });
  } else {
    const v = pick(
      [
        "A forward thermodynamic arrow drives steady entropic decay, giving deep emotional weight to maintenance, repair, and generational memory.",
        "Craftspeople take sacred pride in restoration, fighting against rust, erosion, and forgetfulness with patient daily labor.",
        "Time is lived as an unrepeatable gift; every completed suspension bolt is a small victory against natural wear.",
        "Chronological archives are preserved on etched mica tablets, ensuring hard-won lessons are passed safely to future fledglings.",
      ],
      rng
    );
    rules.push({ label: "Entropic Forward Arrow", text: v });
  }

  // 8. Habitability condition derivation
  if (ctx.condition === "hostile") {
    const v = pick(
      [
        "Precluding carbon biology, existence in this hostile regime is conceived as self-organizing electromagnetic field patterns dancing across ionized gas bands.",
        "Life here exists as coherent standing waves in radiation flows, experiencing thoughts as fluctuating voltage harmonics rather than chemical signals.",
        "Physical bodies are transient plasmonic vortices that stabilize around geothermal magnetic nodes to exchange memories.",
        "Culture is woven from pure mathematical and acoustic resonances, entirely free from the vulnerabilities of biological flesh.",
      ],
      rng
    );
    rules.push({ label: "Speculative Field Existence", text: v });
  } else if (ctx.condition === "marginal") {
    const v = pick(
      [
        "Marginal habitability confines civilization to heavily shielded hanging habitats and deep mantle vaults, where life is sustained by geothermal steam flumes.",
        "Every breath outside pressurized domes requires double-filtered breathing apparatuses and radiation-deflecting silicate cloaks.",
        "Agriculture is strictly subterranean: cultivating moisture-trapping lichens and chemosynthetic algae along boiling volcanic conduits.",
        "Communal laws mandate strict recycling of all biological moisture, trace minerals, and thermal exhaust.",
      ],
      rng
    );
    rules.push({ label: "Sheltered Rift Enclaves", text: v });
  } else {
    const v = pick(
      [
        "Favorable cosmological habitability allows open-air cliff terraces to flourish under mild winds, supported by robust lichen biomes and freshwater flumes.",
        "Inhabitants move freely between hanging dwellings without respirators, enjoying warm thermal updrafts and clean condensation streams.",
        "Open-air gardens of bioluminescent moss and hanging sky-orchards line every residential terrace, providing abundant nourishment.",
        "Daily life celebrates physical vitality: acrobatic glider flights, open-air assembly hearths, and communal feasts beneath twin suns.",
      ],
      rng
    );
    rules.push({ label: "Flourishing Terraced Biosphere", text: v });
  }

  return rules;
}

/**
 * Generate Strange Alternate Self narrative result with complete Dossier,
 * full answer links, and physics-driven derivation.
 */
export function generateStrange(ctx: NarrativeContext): NarrativeResult {
  const { resolved, axes, condition, universe, sharedUniverse } = ctx;

  // Stream factories
  const nameRng = ctx.stream("name");
  const identityRng = ctx.stream("identity");
  const occRng = ctx.stream("occupation");
  const titleRng = ctx.stream("title");
  const taglineRng = ctx.stream("tagline");
  const originRng = ctx.stream("origin");
  const turnRng = ctx.stream("turningPoint");
  const todayRng = ctx.stream("today");
  const choicesRng = ctx.stream("choices");
  const traitsRng = ctx.stream("traits");
  const dossierRng = ctx.stream("dossier");

  // 1. Name & Identity selection
  const identity = pick(STRANGE_IDENTITIES, identityRng);
  const personalName =
    identity.substrate === "field" || identity.substrate === "silicon"
      ? generateAlienName(nameRng)
      : nameRng() > 0.4
      ? generateAlienName(nameRng)
      : generatePersonName(nameRng);

  // 2. Title & Tagline
  const titlePfx = pick(STRANGE_TITLES_PARTS.prefixes, titleRng);
  const titleMod = pick(STRANGE_TITLES_PARTS.modifiers, titleRng);
  const title = `${titlePfx} ${titleMod}`;

  const tagSetting = pick(STRANGE_TAGLINE_PARTS.settings, taglineRng);
  const tagStance = pick(STRANGE_TAGLINE_PARTS.stances, taglineRng);
  const tagline = `${tagSetting} ${tagStance}`;

  // 3. Occupation: domain from ALL resolved.interests x identity x lifePath manner
  const interestDomains: string[] = [];
  const interestMethods: string[] = [];
  for (const intId of resolved.interests) {
    const pool = OCCUPATION_PARTS[intId] || OCCUPATION_PARTS.science;
    interestDomains.push(pick(pool.domains, occRng));
    interestMethods.push(pick(pool.methods, occRng));
  }
  const primaryDomain = interestDomains[0] || "Atmospheric Cartographer";
  const lifeManner = LIFE_PATH_MANNER[resolved.lifePath] || LIFE_PATH_MANNER.rooted;
  const occupation = `${lifeManner.modifier} ${primaryDomain}`;

  // 4. Traits: exactly 4 from resolved.strengths via STRENGTH_TRAIT_MAP
  const traits: Trait[] = [];
  const usedTraitNames = new Set<string>();
  const strengthsPool = [...resolved.strengths];
  while (strengthsPool.length < 4) {
    strengthsPool.push(strengthsPool[strengthsPool.length % resolved.strengths.length]);
  }

  for (let i = 0; i < 4; i++) {
    const sId = strengthsPool[i];
    const bank = STRENGTH_TRAIT_MAP[sId] || STRENGTH_TRAIT_MAP.curiosity;
    const available = bank.filter((t) => !usedTraitNames.has(t.name));
    const traitItem = available.length > 0 ? pick(available, traitsRng) : pick(bank, traitsRng);
    usedTraitNames.add(traitItem.name);

    const baseLevel =
      sId === "curiosity"
        ? axes.curiosity
        : sId === "courage"
        ? axes.courage
        : sId === "discipline" || sId === "patience"
        ? axes.stability
        : sId === "empathy" || sId === "humor"
        ? axes.connection
        : axes.influence;

    const jitter = Math.floor(traitsRng() * 14) - 7;
    traits.push({
      name: traitItem.name,
      description: traitItem.description,
      level: clamp(baseLevel + jitter, 25, 95),
    });
  }

  // 5. Physics derivations
  const physicsRules = derivePhysicsRealities(ctx);

  // 6. Story: origin, turningPoint, today assembled from 3+ recombinable parts
  const originSetting = pick(ORIGIN_PARTS.settings, originRng);
  const originBeat = pick(ORIGIN_PARTS.formativeBeats, originRng);
  const originOutcome = pick(ORIGIN_PARTS.outcomes, originRng);
  const origin = fill(
    `${originSetting} ${originBeat} ${originOutcome}`,
    { name: personalName }
  );

  const turnCatalyst = pick(TURNING_POINT_PARTS.catalysts, turnRng);
  const turnAction = pick(TURNING_POINT_PARTS.actions, turnRng);
  const turnConsequence = pick(TURNING_POINT_PARTS.consequences, turnRng);
  const turningPoint = fill(
    `${turnCatalyst} ${turnAction} ${turnConsequence}`,
    { name: personalName }
  );

  const todayRoutine = pick(TODAY_PARTS.routines, todayRng);
  const todayPerspective = pick(TODAY_PARTS.perspectives, todayRng);
  const today = fill(
    `${todayRoutine} ${todayPerspective}`,
    { name: personalName }
  );

  // 7. Choices: exactly 3 distinct kinds
  const choicesPicks = pickDistinct(STRANGE_CHOICES_BANK, 3, choicesRng);
  const choices: LifeChoice[] = choicesPicks.map((c) => ({
    age: c.age,
    choice: fill(c.choice, { name: personalName }),
    roadNotTaken: fill(c.roadNotTaken, { name: personalName }),
    consequence: fill(c.consequence, { name: personalName }),
  }));

  // 8. World details
  const extraDetails: { label: string; value: string }[] = physicsRules.slice(0, 3).map((r) => ({
    label: r.label,
    value: r.text,
  }));

  const interestPhraseList = listJoin(resolved.interests.map((id) => INTEREST_PHRASE[id].noun));
  const interestDetail = `Applying principles of ${interestPhraseList} to ${identity.dailyCadence}.`;
  const worldDetails: WorldDetail[] = buildWorldDetails(
    "strange",
    universe,
    condition,
    interestDetail,
    extraDetails
  );
  const universeDescription = buildUniverseDescription(universe, condition, sharedUniverse);

  // 9. Dossier construction
  // Skills: 4-6 from interests
  const skillCandidates: string[] = [];
  for (const intId of resolved.interests) {
    const sPool = STRANGE_SKILLS_BANK[intId] || STRANGE_SKILLS_BANK.science;
    skillCandidates.push(...sPool);
  }
  const skills = pickDistinct(skillCandidates, Math.min(5, Math.max(4, skillCandidates.length)), dossierRng);

  // Motivations: 2-3 from ambition
  const ambMotivationsPool = STRANGE_MOTIVATIONS_BANK[resolved.ambition] || STRANGE_MOTIVATIONS_BANK.understand;
  const motivations = pickDistinct(ambMotivationsPool, 2 + (dossierRng() > 0.5 ? 1 : 0), dossierRng);

  // Relationships: exactly 3 distinct roles
  const relPicks = pickDistinct(STRANGE_RELATIONSHIPS_BANK, 3, dossierRng);
  const relationships: Relationship[] = relPicks.map((r) => {
    const who = generateAlienName(dossierRng);
    const bond = fill(r.bondTemplate, { who, name: personalName });
    return {
      who,
      role: r.role,
      bond,
    };
  });

  // Achievements: 2-3
  const achievements = pickDistinct(STRANGE_ACHIEVEMENTS_BANK, 2 + (dossierRng() > 0.4 ? 1 : 0), dossierRng);

  // Failures: exactly 2
  const failures = pickDistinct(STRANGE_FAILURES_BANK, 2, dossierRng);

  // Dilemma: 1
  const dilemma = pick(STRANGE_DILEMMAS_BANK, dossierRng);

  // Trajectory: nearFuture (1-3 sentences) + longArc (1-3 sentences)
  const nearFuture = `Over the next several cycles, ${personalName} will lead a dedicated survey team to anchor new suspension dampeners along the Great Rift, directly training four emerging scouts in high-altitude shear navigation.`;
  const longArc = `Decades hence, when shifting planetary constants further warp the regional horizon, ${personalName}'s hand-carved acoustic flutes and open-source wind charts will remain the definitive bedrock of cliff-dweller survival across the entire world-loop.`;

  // Background paragraph (2-4 sentences)
  const bgSentence1 = `Formed as a ${identity.name} (${identity.bodyDesc}), ${personalName} grew up navigating ${identity.socialUnit}.`;
  const bgSentence2 = `Early development centered on mastering ${identity.sensoryMode}, while apprenticing along the hanging terraces of "${universe.name}".`;
  const bgSentence3 = `This upbringing instilled an unshakeable bond with ${lifeManner.style}, combining local mineral craft with continuous barometric vigilance.`;
  const background = `${bgSentence1} ${bgSentence2} ${bgSentence3}`;

  // Personality paragraph (2-3 sentences)
  const strengthAssetList = listJoin(resolved.strengths.map((s) => STRENGTH_PHRASE[s].habit));
  const personality = `Exhibits an unhurried, razor-sharp presence shaped by ${strengthAssetList}. While calm during severe barometric emergencies, ${personalName} speaks with absolute authority when communal suspension cables or water flumes are threatened. A profound reverence for the physical elegance of this cosmos tempers every pragmatic decision.`;

  // Influence lead + 2-3 specific sentences linking visitor answers
  const baseInfluence = influenceLead(ctx);
  const specificInfluence: string[] = [];

  const allInterestsStr = listJoin(resolved.interests.map((i) => INTEREST_PHRASE[i].noun));
  specificInfluence.push(
    `Every one of your chosen interests (${allInterestsStr}) directly shaped this persona's trade as a ${occupation.toLowerCase()}, dictating how they interact with the world's non-standard physics.`
  );

  specificInfluence.push(
    `Your declared walk as "${LIFE_PATH_PHRASE[resolved.lifePath].role}" guided their life manner (${lifeManner.modifier}), prioritizing ${lifeManner.bias}.`
  );

  if (resolved.filled.ambition) {
    specificInfluence.push(
      `Because ambition was left open, the generator assigned the drive to "${AMBITION_PHRASE[resolved.ambition].goal}", translating into their defining dilemma of balancing exploration against civic duty.`
    );
  } else {
    specificInfluence.push(
      `Your personal ambition to "${AMBITION_PHRASE[resolved.ambition].goal}" directly framed ${personalName}'s motivations and their defining tension over ${dilemma.toLowerCase().replace(/\.$/, "")}.`
    );
  }

  const influence = [...baseInfluence, ...specificInfluence];

  const dossier: Dossier = {
    background,
    personality,
    skills,
    motivations,
    relationships,
    achievements,
    failures,
    dilemma,
    trajectory: {
      nearFuture,
      longArc,
    },
    influence,
  };

  return {
    name: personalName,
    title,
    tagline,
    occupation,
    traits,
    story: {
      origin,
      turningPoint,
      today,
    },
    choices,
    worldDetails,
    universeDescription,
    dossier,
  };
}
